// components/Charts.js
import React from 'react';
import {Box} from '@chakra-ui/react';
import {Line, Bar, Pie} from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    ArcElement,
    Title,
    Tooltip,
    Legend
} from 'chart.js';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    ArcElement,
    Title,
    Tooltip,
    Legend
);

export const LineChart = ({data, xKey, yKey, color, unit}) => {
    const chartData = {
        labels: data.map(item => item[xKey]),
        datasets: [
            {
                label: unit ? `(${unit})` : '',
                data: data.map(item => item[yKey]),
                borderColor: color,
                backgroundColor: color + '20',
                tension: 0.3,
                fill: true
            }
        ]
    };

    const options = {
        responsive: true,
        plugins: {
            legend: {
                display: false
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                title: {
                    display: !!unit,
                    text: unit
                }
            }
        }
    };

    return <Line data={chartData} options={options}/>;
};

// Updated LineChart component in Charts.js
export const LineChart2 = ({data, xKey, yKeys = [], colors = [], unit}) => {
    const chartData = {
        labels: data.map(item => item[xKey]),
        datasets: yKeys.map((key, index) => ({
            label: key,
            data: data.map(item => item[key]),
            borderColor: colors[index] || `hsl(${index * 90}, 70%, 50%)`,
            backgroundColor: (colors[index] || `hsl(${index * 90}, 70%, 50%)`) + '20',
            tension: 0.3,
            fill: index === 0 // Only fill the first dataset for better visibility
        }))
    };

    const options = {
        responsive: true,
        plugins: {
            legend: {
                position: 'top',
            },
            tooltip: {
                callbacks: {
                    label: (context) => {
                        return `${context.dataset.label}: ${new Intl.NumberFormat('es-MX', {
                            style: 'currency',
                            currency: 'MXN'
                        }).format(context.raw)}`;
                    }
                }
            }
        },
        scales: {
            y: {
                beginAtZero: false,
                ticks: {
                    callback: (value) => {
                        return new Intl.NumberFormat('es-MX', {
                            style: 'currency',
                            currency: 'MXN',
                            maximumFractionDigits: 0
                        }).format(value);
                    }
                }
            }
        }
    };

    return <Line data={chartData} options={options}/>;
};

// components/Charts.js
// import {Bar} from 'react-chartjs-2';
// import {Chart as ChartJS} from 'chart.js/auto'; // Important: This registers everything automatically

export const BarChart = ({data, colors}) => {
    const chartData = {
        labels: data.map(item => item.name),
        datasets: [{
            data: data.map(item => item.value),
            backgroundColor: colors, // Now using the passed colors
            borderColor: colors.map(color => `${color}Dark`), // Slightly darker borders
            borderWidth: 1,
            borderRadius: 4, // Rounded corners
            barPercentage: 0.7 // Controls bar width
        }]
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false
            },
            tooltip: {
                callbacks: {
                    label: (context) => {
                        return new Intl.NumberFormat('es-MX', {
                            style: 'currency',
                            currency: 'MXN'
                        }).format(context.raw);
                    }
                }
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    callback: (value) => {
                        if (value >= 1000000) return `$${value / 1000000}M`;
                        if (value >= 1000) return `$${value / 1000}k`;
                        return `$${value}`;
                    }
                }
            }
        }
    };

    return <Bar data={chartData} options={options}/>;
};

export const PieChart = ({data, colors}) => {
    const chartData = {
        labels: data.map(item => item.name),
        datasets: [
            {
                data: data.map(item => item.value),
                backgroundColor: colors
            }
        ]
    };

    const options = {
        responsive: true,
        plugins: {
            legend: {
                display: false
            }
        }
    };

    return <Pie data={chartData} options={options}/>;
};